import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { fill } from 'lodash-es'
import { useCatalogStore, useFilterStore, useSortStore, useUserStore } from '..'
import { CompactCruise, FindCruisesRequest, OrderDirection } from '../../bewotec/openapi'
import { handleError } from '../../general/errorHandling'
import { SORT_KEYS } from '../../general/helper'
import { mapAgencyForRequest } from '../../store/app/helper'
import { useAppStore } from '../app'
import { appHelper } from '../app/helper'
import { useCruiseListStore } from '../cruise-list'
import { defaultAverageLineHeight, defaultPageSize, initialPageSize, pageSizeMultiplier } from '../filter/defaultValues'
import { getFormattedDate, getMaxDuration, getMinDuration, getSortKey, preprocessCatalog } from './helper.js'

dayjs.locale('de')

export const fetchCatalog = async () => {
  const userStore = useUserStore()
  const sortStore = useSortStore()
  const filterStore = useFilterStore()
  const cruiseListStore = useCruiseListStore()
  const catalogStore = useCatalogStore()
  const appStore = useAppStore()

  const isInitialLoading = catalogStore.pageNumber === 1

  if (isInitialLoading) {
    appStore.startupMessage = 'Lade Cruises...'
    catalogStore.totalResults = 0
  }

  catalogStore.loadingNextPageInProgress = true

  let bewotecCatalog: CompactCruise[]
  const filter = filterStore.filterFields
  const adults = !!filter.adults.value ? parseInt(filter.adults.value, 10) : 0
  const juveniles = !!filter.juveniles.value ? parseInt(filter.juveniles.value, 10) : 0
  const children = !!filter.children.value ? parseInt(filter.children.value, 10) : 0
  const babies = !!filter.babies.value ? parseInt(filter.babies.value, 10) : 0

  if (!adults && !juveniles && !children && !babies) {
    clearCatalogResults()

    appStore.startupMessage = 'Keine Reisenden ausgewählt.'
    catalogStore.loadingNextPageInProgress = false
    return
  }

  const sortKey = sortStore.sortKey
  const isSortingByPriceModel = !!sortKey && ![SORT_KEYS.abfahrt, SORT_KEYS.route].includes(sortKey)

  const findCruisesReq: FindCruisesRequest = {
    agencyId: mapAgencyForRequest(userStore.agencyId),
    earliestDepartureDate: !!filter.departureDate.value ? getFormattedDate(filter.departureDate.value) : undefined,
    latestArrivalDate: !!filter.arrivalDate.value ? getFormattedDate(filter.arrivalDate.value) : undefined,
    priceModelCodes: filter.priceModels.value,
    minDuration: getMinDuration(filter.duration.value),
    maxDuration: getMaxDuration(filter.duration.value),
    regions: filter.regions.value,
    gateways: filter.airports.value,
    cabinCategoryGroups: filter.categories.value,
    cruiseAttributes: filter.cruiseAttributes.value,
    campaigns: filter.campaigns.value,
    ships: filter.ships.value,
    adults: adults ?? 2,
    juniors: juveniles ?? 0,
    children: children ?? 0,
    babies: babies ?? 0,
    pageNumber: isInitialLoading ? 1 : catalogStore.pageNumber,
    pageSize: isInitialLoading ? initialPageSize : defaultPageSize,
    sortBy: getSortKey(sortKey, isSortingByPriceModel),
    sortByAddition: isSortingByPriceModel ? sortKey : undefined,
    order: sortStore.sortOrder === -1 ? OrderDirection.DESC : OrderDirection.ASC,
    flexFlight: filter.hasFlexFlight.value,
    higherFlightClasses: filter.hasHigherFlightClasses.value,
    xTrackingIdentifier: catalogStore.requestId,
    ports: !filter.isEndPort.value && !filter.isStartPort.value ? filter.ports.value : undefined,
    departurePorts: filter.isStartPort.value ? filter.ports.value : undefined,
    arrivalPorts: filter.isEndPort.value ? filter.ports.value : undefined,
  }

  try {
    const bewotecCatalogRaw = await appHelper.bewotecMicroServiceApi().findCruisesRaw(findCruisesReq)
    const responseHeaders = bewotecCatalogRaw.raw.headers

    // in case of race condition
    if (responseHeaders.get('x-tracking-identifier') !== catalogStore.requestId) {
      return
    }

    const totalResults = responseHeaders.get('x-total-results')
    const parsedTotalResults = !!totalResults ? parseInt(totalResults, 10) : 0
    catalogStore.totalResults = parsedTotalResults

    bewotecCatalog = await bewotecCatalogRaw.value()
  } catch (error) {
    clearCatalogResults()
    handleError({ error, origin: 'fetchCatalog - bewotecCatalog' })
    appStore.startupMessage = 'Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut...'

    return
  }

  if (isInitialLoading && !bewotecCatalog.length) {
    clearCatalogResults()
    appStore.startupMessage = 'Keine Cruises gefunden.'
    return
  }

  const catalog = preprocessCatalog(bewotecCatalog)

  if (isInitialLoading) {
    cruiseListStore.listItemHeight = fill(Array(catalogStore.totalResults), 0)
    cruiseListStore.averageLineHeight = defaultAverageLineHeight

    catalogStore.cruises = catalog
  } else {
    catalogStore.cruises = catalogStore.cruises.concat(catalog)
    cruiseListStore.calcListItemHeight(cruiseListStore.listItemHeight)
  }

  catalogStore.loadingNextPageInProgress = false
  appStore.startupMessage = ''
}

export const fetchNextPage = async () => {
  const catalogStore = useCatalogStore()

  if (catalogStore.loadingNextPageInProgress) return

  const currentPageNumber = catalogStore.pageNumber
  const currentCruiseResults = currentPageNumber * defaultPageSize

  if (currentCruiseResults < catalogStore.totalResults) {
    const nextPageNumber = currentPageNumber + (currentPageNumber > 1 ? 1 : pageSizeMultiplier)
    catalogStore.pageNumber = nextPageNumber

    await catalogStore.fetchCatalog()
  }
}

const clearCatalogResults = () => {
  const catalogStore = useCatalogStore()

  catalogStore.totalResults = 0
  catalogStore.cruises = []
}

export default {
  fetchCatalog,
  fetchNextPage,
}

// ------- exports for testing purposes -------
export const exportForTesting = {
  clearCatalogResults,
}
